import { version } from "@conorh/bug-reporter-react/package.json";
import { collectBugReportDiagnostics } from "@conorh/bug-reporter-react/diagnostics";
import type { BugReportPayload } from "@conorh/bug-reporter-react";
import type { MountedBugReporter } from "@conorh/bug-reporter-react/standalone";

export function collectSwpDiagnostics() {
  const diagnostics = collectBugReportDiagnostics({ surface: "swp" });
  // Search terms and fragments can contain patient details or access tokens.
  const pagePath = window.location.pathname;
  return {
    ...diagnostics,
    pageUrl: `${window.location.origin}${pagePath}`,
    pagePath,
    route: pagePath,
    recentConsoleErrors: [],
    appRevision: document.querySelector<HTMLMetaElement>('meta[name="app-revision"]')?.content,
  };
}

export async function submitSwpBugReport(payload: BugReportPayload) {
  const formData = new FormData();
  formData.append("bug_report[description]", payload.description);
  formData.append("bug_report[page_path]", payload.pagePath);
  formData.append("bug_report[client_diagnostics]", JSON.stringify(payload.clientDiagnostics));
  if (payload.screenshot) {
    if (payload.screenshot.size > 5 * 1024 * 1024) throw new Error("Screenshot must be smaller than 5 MB. Upload a smaller image or remove the screenshot.");
    const extension = payload.screenshot.type === "image/jpeg" ? "jpg" : payload.screenshot.type === "image/webp" ? "webp" : "png";
    formData.append("screenshot", payload.screenshot, `bug-report.${extension}`);
  }

  const response = await fetch("/bug_reports", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
    },
    body: formData,
  });
  if (response.redirected || response.status === 401) throw new Error("Your session has expired. Sign in again before sending.");
  if (!response.headers.get("content-type")?.includes("application/json")) throw new Error("Unable to send the report. Please try again.");
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Unable to send the report. Please try again.");
  return result.message as string;
}

export function mountSwpBugReporter(element: HTMLElement) {
  let reporter: MountedBugReporter | undefined;
  let pending: Promise<void> | undefined;
  let destroyed = false;
  let wantsOpen = false;
  const status = document.createElement("p");
  status.setAttribute("role", "status");
  status.className = "fixed bottom-16 right-4 z-[120] max-w-xs rounded-lg bg-white p-3 text-sm text-red-700 shadow-lg";
  status.hidden = true;
  element.append(status);

  const ensureMounted = () => {
    if (pending || reporter || destroyed) return pending;
    const url = `/bug-reporter/${version}/index.js`;
    pending = import(/* @vite-ignore */ url).then((module: typeof import("@conorh/bug-reporter-react/standalone")) => {
      if (destroyed) return;
      status.hidden = true;
      reporter = module.mountBugReporter(element, {
        submitReport: submitSwpBugReport,
        collectDiagnostics: collectSwpDiagnostics,
        surface: "swp",
        // Keep form values visible for debugging; the shared reporter excludes
        // passwords, file inputs, and explicitly private elements by default.
        excludeSelectors: ['[data-patient-navbar-search]'],
      });
      if (wantsOpen) void reporter.open();
    }).catch(() => {
      if (destroyed) return;
      status.textContent = "Unable to load bug reporting. Use Report a bug to try again.";
      status.hidden = false;
    }).finally(() => { pending = undefined; });
    return pending;
  };
  const open = () => {
    wantsOpen = true;
    if (reporter) void reporter.open();
    else void ensureMounted();
  };
  document.addEventListener("swp:report-bug", open);
  void ensureMounted();
  return () => {
    destroyed = true;
    document.removeEventListener("swp:report-bug", open);
    reporter?.destroy();
    status.remove();
  };
}
